import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  useTheme,
  Skeleton,
} from "@mui/material";
import {
  LocalHospital,
  People,
  ExitToApp,
  Bed,
  Assignment,
  Refresh,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../context";
import { getIPDDashboard } from "../../../api/ipd";

// StatCard component matching the reception overview pattern
const StatCard = ({
  icon,
  title,
  value,
  amount,
  onClick,
  theme,
  loading = false,
  highlight,
  cardSx,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <Card sx={{ height: "100%", p: 2, borderRadius: 3, boxShadow: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={180} />
      </Card>
    );
  }

  // Capitalize each word in the title
  const capitalizeTitle = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  // Helper to render amount with proper formatting
  const renderAmount = (amt, idx) => {
    return (
      <Typography
        key={idx}
        variant={idx === 0 ? "h4" : "body1"}
        fontWeight="bold"
        sx={{
          color: cardSx?.color || theme.palette.primary.contrastText,
          lineHeight: 1.2,
          mt: idx > 0 ? 1 : 0,
          fontSize: cardSx ? 32 : undefined,
          textShadow: cardSx ? "0 2px 8px rgba(0,0,0,0.10)" : undefined,
        }}
      >
        {amt}
      </Typography>
    );
  };

  const isSummaryCard = !!cardSx;

  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "default",
        transition: "all 0.3s ease",
        border: 0,
        borderRadius: 4,
        boxShadow: isSummaryCard ? "0 8px 32px 0 rgba(31, 38, 135, 0.15)" : 2,
        p: isSummaryCard ? 4 : 2,
        background: isSummaryCard
          ? `linear-gradient(135deg, ${cardSx?.bgcolor} 60%, ${theme.palette.background.paper} 100%)`
          : cardSx?.bgcolor || theme.palette.primary.main,
        color: cardSx?.color || theme.palette.primary.contrastText,
        position: "relative",
        borderTop: isSummaryCard
          ? `3px solid ${cardSx?.borderLeft || cardSx?.bgcolor}`
          : undefined,
        borderBottom: isSummaryCard
          ? `3px solid ${cardSx?.borderLeft || cardSx?.bgcolor}`
          : undefined,
        ...cardSx,
        "&:hover": {
          boxShadow: 2,
        },
      }}
      aria-label={`${title} card`}
    >
      {/* Icon in a circle */}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: isSummaryCard
            ? cardSx?.borderLeft || cardSx?.bgcolor
            : theme.palette.primary.contrastText,
          color: isSummaryCard
            ? cardSx?.color || "#fff"
            : theme.palette.primary.main,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 36,
          boxShadow: isSummaryCard ? 3 : 1,
          mb: 2,
        }}
      >
        {icon}
      </Box>

      {/* Title */}
      <Typography
        variant="h6"
        fontWeight={700}
        sx={{
          color: cardSx?.color || theme.palette.primary.contrastText,
          textAlign: "center",
          mb: 1,
          letterSpacing: 1,
          fontSize: 22,
          textShadow: isSummaryCard ? "0 2px 8px rgba(0,0,0,0.10)" : undefined,
        }}
      >
        {capitalizeTitle(title)}
      </Typography>

      {/* Value/Amount */}
      <Box sx={{ textAlign: "center", mb: 2 }}>
        {Array.isArray(amount) ? (
          <Box>{amount.map((amt, idx) => renderAmount(amt, idx))}</Box>
        ) : (
          renderAmount(amount, 0)
        )}
      </Box>
    </Card>
  );
};

const IPDDashboard = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = useAuth();
  const token = auth?.user?.token || "";

  const {
    data: dashboardData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["ipd-dashboard"],
    queryFn: () => getIPDDashboard(token),
    refetchInterval: 30000, // Refresh every 30 seconds
    retry: 3, // Retry up to 3 times on error
  });

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", backgroundColor: "#f5f5f5", p: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Paper sx={{ borderRadius: "15px", width: "100%" }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                  {t("ipd.dashboard.title", "IPD Dashboard")}
                </Typography>
                <Grid container spacing={3}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Grid key={index} item xs={12} sm={6} md={4} lg={2}>
                      <StatCard loading />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (isError) {
    // Show dashboard with empty data instead of error message
    const emptyData = {
      total_admissions: 0,
      current_admissions: 0,
      discharged_today: 0,
      available_beds: 0,
      occupied_beds: 0,
      total_revenue: 0,
      pending_payments: 0,
    };

    return (
      <Box sx={{ width: "100%", backgroundColor: "#f5f5f5", p: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Paper sx={{ borderRadius: "15px", width: "100%" }}>
              <Box sx={{ p: 3 }}>
                {/* Header Section */}
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  mb={4}
                  gap={2}
                >
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: theme.palette.text.primary }}
                  >
                    {t("ipd.dashboard.title", "IPD Dashboard")}
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Tooltip title={t("ipd.dashboard.refresh", "Refresh")}>
                      <IconButton onClick={() => refetch()} color="primary">
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Show error message */}
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {t(
                    "ipd.dashboard.error_loading",
                    "Unable to load dashboard data. Showing empty dashboard. Please check your connection and try refreshing."
                  )}
                  {error && (
                    <Box sx={{ mt: 1, fontSize: "0.875rem" }}>
                      {t("common.error", "Error")}:{" "}
                      {error.message ||
                        t("common.unknown_error", "Unknown error")}
                    </Box>
                  )}
                </Alert>

                {/* Admissions Cards */}
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                  {t(
                    "ipd.dashboard.admissions_overview",
                    "Admissions Overview"
                  )}
                </Typography>
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {[
                    {
                      icon: <LocalHospital />,
                      title: t(
                        "ipd.dashboard.total_admissions",
                        "Total Admissions"
                      ),
                      value: emptyData.total_admissions,
                      amount: emptyData.total_admissions.toString(),
                    },
                    {
                      icon: <People />,
                      title: t(
                        "ipd.dashboard.current_admissions",
                        "Current Admissions"
                      ),
                      value: emptyData.current_admissions,
                      amount: emptyData.current_admissions.toString(),
                    },
                    {
                      icon: <ExitToApp />,
                      title: t(
                        "ipd.dashboard.discharged_today",
                        "Discharged Today"
                      ),
                      value: emptyData.discharged_today,
                      amount: emptyData.discharged_today.toString(),
                    },
                    {
                      icon: <Bed />,
                      title: t(
                        "ipd.dashboard.available_beds",
                        "Available Beds"
                      ),
                      value: emptyData.available_beds,
                      amount: emptyData.available_beds.toString(),
                    },
                    {
                      icon: <Bed />,
                      title: t("ipd.dashboard.occupied_beds", "Occupied Beds"),
                      value: emptyData.occupied_beds,
                      amount: emptyData.occupied_beds.toString(),
                    },
                    {
                      icon: <Assignment />,
                      title: t(
                        "ipd.dashboard.ward_rounds_today",
                        "Ward Rounds Today"
                      ),
                      value: 0,
                      amount: "0",
                    },
                  ].map((item, index) => (
                    <Grid key={index} item xs={12} sm={6} md={4} lg={2}>
                      <StatCard
                        icon={item.icon}
                        title={item.title}
                        value={item.value}
                        amount={item.amount}
                        theme={theme}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Use real data from API
  const realData = dashboardData || {
    total_admissions: 0,
    current_admissions: 0,
    discharged_today: 0,
    available_beds: 0,
    occupied_beds: 0,
    total_revenue: 0,
    pending_payments: 0,
  };

  const admissionCards = [
    {
      icon: <LocalHospital />,
      title: t("ipd.dashboard.total_admissions", "Total Admissions"),
      value: realData.total_admissions,
      amount: realData.total_admissions.toString(),
    },
    {
      icon: <People />,
      title: t("ipd.dashboard.current_admissions", "Current Admissions"),
      value: realData.current_admissions,
      amount: realData.current_admissions.toString(),
    },
    {
      icon: <ExitToApp />,
      title: t("ipd.dashboard.discharged_today", "Discharged Today"),
      value: realData.discharged_today,
      amount: realData.discharged_today.toString(),
    },
    {
      icon: <Bed />,
      title: t("ipd.dashboard.available_beds", "Available Beds"),
      value: realData.available_beds,
      amount: realData.available_beds.toString(),
    },
    {
      icon: <Bed />,
      title: t("ipd.dashboard.occupied_beds", "Occupied Beds"),
      value: realData.occupied_beds,
      amount: realData.occupied_beds.toString(),
    },
    {
      icon: <Assignment />,
      title: t("ipd.dashboard.ward_rounds_today", "Ward Rounds Today"),
      value: realData.ward_rounds_today || 0,
      amount: (realData.ward_rounds_today || 0).toString(),
    },
  ];

  return (
    <Box sx={{ width: "100%", backgroundColor: "#f5f5f5", p: 3 }}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Paper sx={{ borderRadius: "15px", width: "100%" }}>
            <Box sx={{ p: 3 }}>
              {/* Header Section */}
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                mb={4}
                gap={2}
              >
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: theme.palette.text.primary }}
                >
                  {t("ipd.dashboard.title", "IPD Dashboard")}
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Tooltip title={t("ipd.dashboard.refresh", "Refresh")}>
                    <IconButton onClick={() => refetch()} color="primary">
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Admissions Cards */}
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>
                {t("ipd.dashboard.admissions_overview", "Admissions Overview")}
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {admissionCards.map((item, index) => (
                  <Grid key={index} item xs={12} sm={6} md={4} lg={2}>
                    <StatCard
                      icon={item.icon}
                      title={item.title}
                      value={item.value}
                      amount={item.amount}
                      theme={theme}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IPDDashboard;
